import { useState } from "react";
import { Box, CircularProgress, Button as MUIButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useSnackbar } from "$utils/useSnackbar";
import { Text, Modal } from "$components";
import { Switch } from "$components/Forms/Switch";
import useAuth from "$hooks/useAuth";
import useDeletableProduct from "$hooks/Catalogue/useDeletableProduct";
import { theme } from "$utils/theme/bataillog";
import {
  snackbarErrorOptions,
  snackbarSuccessOptions,
} from "$utils/SnackbarOptions";
import productApi, { editProduct } from "../../../../api/productApi";
import { ProductService } from "$services";
import JModal from "$components/JModal/JModal";
import useJStyles from "$hooks/useJStyles";
import { useProductFormContext } from "../contexts/ProductFormContext";

export const ProductFormHeader = () => {
  const { product, isReferenced, company, onReset } = useProductFormContext();
  const { isAdmin, token } = useAuth();
  const { jclasses } = useJStyles();

  const [openModalDeleteProduct, setOpenModalDeleteProduct] = useState(false);
  const [openSnackbarError] = useSnackbar(snackbarErrorOptions);
  const [openSnackbar] = useSnackbar(snackbarSuccessOptions);

  const { isProductDeletable, isProductDeletableLoading } = useDeletableProduct(
    product,
    company?._id
  );

  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const [showConflictModal, setShowConflictModal] = useState<boolean>(false);
  const [conflicts, setConflicts] = useState<string[]>([]);

  const toggleProductActive = async () => {
    if (!product) return;
    try {
      await editProduct(
        token,
        { ...product, isActive: !product.isActive },
        product._id
      );
      if (onReset) onReset();
    } catch (e) {
      console.error(e);
    }
  };

  const deleteProduct = async () => {
    if (!product) return;
    const { _id, reference } = product;
    const deleteProductRes = await productApi.deleteProduct(token, _id);
    if (deleteProductRes.status === 403) {
      openSnackbarError(
        `Le produit ${reference} est déjà utilisé dans une EDB, impossible de le supprimer.`,
        5000
      );
    } else {
      openSnackbar(`Le produit ${reference} a bien été supprimé.`, 5000);
      onReset();
    }
    setOpenModalDeleteProduct(false);
  };

  const handleProductActive = async () => {
    if (product?.isActive) {
      try {
        const result = await ProductService.checkDeactivation(
          product._id,
          product.reference,
          company?._id
        );
        if (result.canDeactivate) {
          setShowConfirmModal(true);
        } else {
          setConflicts(result.conflicts || []);
          setShowConflictModal(true);
        }
      } catch (error: any) {
        openSnackbar(
          error?.response?.data?.message ||
            "Impossible de désactiver ce produit"
        );
      }
    } else {
      toggleProductActive();
    }
  };

  const isDeletable = isProductDeletable && !isProductDeletableLoading;

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width="100%"
      >
        <Text type="h2">
          {product
            ? "Éditer le produit"
            : isReferenced
              ? "Ajouter un produit"
              : "Ajouter un produit hors catalogue"}
        </Text>

        {product && isAdmin() && (
          <Box
            marginLeft={1}
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={2}
          >
            {product?.categories?.[0]?.title !== "REFERENCEMENT" && (
              <div>
                <Switch
                  label="Produit actif"
                  checked={product?.isActive}
                  onChange={handleProductActive}
                />
              </div>
            )}

            {isProductDeletableLoading ? (
              <CircularProgress size={24} />
            ) : (
              <MUIButton
                style={{
                  color: isDeletable
                    ? theme.color.blueLight
                    : theme.color.gray3,
                  minWidth: 30,
                }}
                variant="text"
                disabled={!isDeletable}
                onClick={() => {
                  setOpenModalDeleteProduct(true);
                }}
              >
                <DeleteIcon />
              </MUIButton>
            )}
          </Box>
        )}
      </Box>

      <Modal
        textValidate="Supprimer"
        textCancel="Annuler"
        open={openModalDeleteProduct}
        onValidate={deleteProduct}
        onCancel={() => setOpenModalDeleteProduct(false)}
      >
        <div className={jclasses.jDialogContent}>
          <Text>
            Attention
            <br />
            Vous êtes sur le point de supprimer définitivement ce produit du
            catalogue.
            <br />
            Êtes-vous sûr de vouloir continuer ?
          </Text>
        </div>
      </Modal>

      {showConfirmModal && (
        <JModal
          onClose={() => setShowConfirmModal(false)}
          onConfirm={() => {
            toggleProductActive();
            setShowConfirmModal(false);
          }}
          labelOk="Désactiver"
          labelCancel="Annuler"
          message="Vous êtes sur le point de désactiver ce produit, êtes-vous sûr de vouloir continuer ?"
        />
      )}
      {showConflictModal && (
        <JModal
          onClose={() => setShowConflictModal(false)}
          onConfirm={() => setShowConflictModal(false)}
          labelOk="Fermer"
          hideClose
          message="Ce produit ne peut pas être désactivé car il est embarqué dans :"
        >
          <div>
            {conflicts.filter((c) => c.startsWith("EDB")).length > 0 && (
              <p>
                <strong>EDB :</strong>{" "}
                {conflicts
                  .filter((c) => c.startsWith("EDB"))
                  .map((c) => c.replace("EDB N°", ""))
                  .join(", ")}
              </p>
            )}
            {conflicts.filter((c) => c.startsWith("Commande")).length > 0 && (
              <p>
                <strong>Commandes :</strong>{" "}
                {conflicts
                  .filter((c) => c.startsWith("Commande"))
                  .map((c) => c.replace("Commande N°", ""))
                  .join(", ")}
              </p>
            )}
          </div>
        </JModal>
      )}
    </>
  );
};
